import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    ChevronWrapper,
    CollapseWrapper,
    SectionTitle,
    SportIconWrapper,
    ViewAllArrowWrapper,
    ViewAllWrapper,
} from 'src/ui/components/collapse/sports/02/Collapse.style';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { AmericanFootballIcon } from 'src/ui/assets/icons/sports/AmericanFootballIcon';
import { BasketballIcon } from 'src/ui/assets/icons/sports/BasketballIcon';
import { BaseballIcon } from 'src/ui/assets/icons/sports/BaseballIcon';
import { BoxingIcon } from 'src/ui/assets/icons/sports/BoxingIcon';
import { CricketIcon } from 'src/ui/assets/icons/sports/CricketIcon';
import { DartsIcon } from 'src/ui/assets/icons/sports/DartsIcon';
import { FootballIcon } from 'src/ui/assets/icons/sports/FootballIcon';
import { GolfIcon } from 'src/ui/assets/icons/sports/GolfIcon';
import { GreyhoundRacingIcon } from 'src/ui/assets/icons/sports/GreyhoundRacingIcon';
import { HorseRacingIcon } from 'src/ui/assets/icons/sports/HorseRacingIcon';
import { MMAIcon } from 'src/ui/assets/icons/sports/MMAIcon';
import { PoliticsIcon } from 'src/ui/assets/icons/sports/PoliticsIcon';
import { RugbyLeagueIcon } from 'src/ui/assets/icons/sports/RugbyLeagueIcon';
import { TennisIcon } from 'src/ui/assets/icons/sports/TennisIcon';
import { CyclingIcon } from 'src/ui/assets/icons/sports/CyclingIcon';
import { FormulaOneIcon } from 'src/ui/assets/icons/sports/FormulaOneIcon';
import { GaelicHurlingIcon } from 'src/ui/assets/icons/sports/GaelicHurlingIcon';
import { IceHockeyIcon } from 'src/ui/assets/icons/sports/IceHockeyIcon';
import { RugbyUnionIcon } from 'src/ui/assets/icons/sports/RugbyUnionIcon';
import { SnookerIcon } from 'src/ui/assets/icons/sports/SnookerIcon';
import { VolleyballIcon } from 'src/ui/assets/icons/sports/VolleyballIcon';
import { GaelicFootballIcon } from 'src/ui/assets/icons/sports/GaelicFootball';
import { MotorSportIcon } from 'src/ui/assets/icons/sports/MotorSportsIcon';
import { WrestlingIcon } from 'src/ui/assets/icons/sports/WrestlingIcon';
import { SportRouteType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { I18n } from 'src/domains/layouts/webview/components/language/I18n';
import { AustralianRulesIcon } from 'src/ui/assets/icons/sports/AustralianRulesIcon';

interface CollapsePropsType {
    className?: string;
    dataTest?: string;
    handleToggle: () => void;
    isOpen: boolean;
    title: React.ReactNode;
    sportId: string;
}

const renderSportIcon = (name: string | null): JSX.Element | undefined => {
    const normalized = name?.toLowerCase().replace(/[ .-]/g, '') ?? '';

    const iconMap: Record<string, JSX.Element> = {
        americanfootball: <AmericanFootballIcon />,
        australianrules: <AustralianRulesIcon />,
        baseball: <BaseballIcon />,
        basketball: <BasketballIcon />,
        boxing: <BoxingIcon />,
        cricket: <CricketIcon />,
        cycling: <CyclingIcon />,
        darts: <DartsIcon />,
        football: <FootballIcon />,
        formulaone: <FormulaOneIcon />,
        gaelicfootball: <GaelicFootballIcon />,
        gaelichurling: <GaelicHurlingIcon />,
        golf: <GolfIcon />,
        greyhoundracing: <GreyhoundRacingIcon />,
        horseracing: <HorseRacingIcon />,
        icehockey: <IceHockeyIcon />,
        mixedmartialarts: <MMAIcon />,
        motorsport: <MotorSportIcon />,
        politics: <PoliticsIcon />,
        rugbyleague: <RugbyLeagueIcon />,
        rugbyunion: <RugbyUnionIcon />,
        snooker: <SnookerIcon />,
        tennis: <TennisIcon />,
        volleyball: <VolleyballIcon />,
        wrestling: <WrestlingIcon />,
    };

    return iconMap[normalized];
};

/**
 * This component will be used for sections:
 * - **sports**
 */
export const Collapse = observer(
    'Collapse',
    ({ title, className, handleToggle, isOpen, dataTest, sportId }: CollapsePropsType): JSX.Element => {
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;

        const routingData: SportRouteType = {
            name: 'sport',
            id: sportId,
            nameType: 'regular',
        };

        return (
            <CollapseWrapper
                data-test={dataTest}
                isOpen={isOpen}
                onClick={handleToggle}
                className={className}
                variant={configState.componentVariants.collapseVariant}
            >
                <ChevronWrapper position={isOpen ? 'up' : 'right'} />
                <SportIconWrapper variant={configState.componentVariants.collapseVariant}>
                    {renderSportIcon(sportId)}
                </SportIconWrapper>
                <SectionTitle data-test='section-title'>{title}</SectionTitle>
                <ViewAllWrapper
                    to={routingData}
                    variant={configState.componentVariants.collapseVariant}
                >
                    <I18n
                        langKey='collapse.link.view.label'
                        defaultText='View All'
                    />
                    <ViewAllArrowWrapper />
                </ViewAllWrapper>
            </CollapseWrapper>
        );
    }
);
